import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';
import { IconRadioCircle20, IconRadioEmptyCircle20 } from 'components/base/Icon';
import List from 'components/base/List';
import BottomSheet from 'components/common/BottomSheet';
import { useSearchQueryString } from 'hooks';
import { SortTypeItem, SORT_TYPE, SORT_TYPE_LIST } from 'util/search_utils';
import { Log, useTracker } from 'util/Tracker';

interface Props {
  active_sort_type: SORT_TYPE;
  search_ubl: Omit<Log, 'category'>;
  sort_type_list?: SortTypeItem[];
  onToggleSheet: (active: boolean) => void;
  open_sort_sheet: boolean;
}

/**
 * 정렬 선택 바텀 시트
 * @component
 */
const SortBottomSheet: React.FC<Props> = ({
  active_sort_type,
  search_ubl,
  sort_type_list = SORT_TYPE_LIST,
  open_sort_sheet,
  onToggleSheet,
}) => {
  const router = useRouter();
  const tracker = useTracker();
  const { search_filter_ubl } = useSearchQueryString();

  const sort_common_ubl = useMemo(() => {
    const { navigation, data: data_attr, ...rest } = search_ubl;
    return { ...rest, navigation: getNavigationUbl(navigation), data: { ...data_attr, ...search_filter_ubl } };
  }, [search_ubl, search_filter_ubl]);

  useEffect(() => {
    if (open_sort_sheet) {
      tracker.addLog({ ...sort_common_ubl, category: 'pageview' });
    }
  }, [open_sort_sheet, sort_common_ubl, tracker]);

  const handleSortClick = async (sort_item: SortTypeItem, object_idx: number) => {
    const { data: common_data_ubl, ...rest } = sort_common_ubl;
    tracker.addLog({
      ...rest,
      category: 'click',
      object_type: 'sort',
      object_idx,
      object_id: sort_item.type,
      data: {
        ...common_data_ubl,
        prev_sort_type: active_sort_type,
        sort_type: sort_item.type,
      },
    });
    router.replace({ pathname: router.pathname, query: { ...router.query, sort_type: sort_item.type } });
    onToggleSheet(false);
  };

  return (
    <BottomSheet active={open_sort_sheet} setIsActive={onToggleSheet} title='상품 정렬' show_close_icon can_scroll>
      <SC.Container>
        <List classname='sort_list'>
          {sort_type_list.map((sort_item, index) => {
            return (
              <List.Item
                key={sort_item.type}
                title={sort_item.text}
                right={active_sort_type === sort_item.type ? <IconRadioCircle20 /> : <IconRadioEmptyCircle20 />}
                onClick={() => handleSortClick(sort_item, index + 1)}
              />
            );
          })}
        </List>
      </SC.Container>
    </BottomSheet>
  );
};

export default SortBottomSheet;

function getNavigationUbl(navigation_prefix: string) {
  if (!navigation_prefix) {
    return '';
  }
  return `${navigation_prefix}_sort_dialog`;
}

const SC = {
  Container: styled.div`
    padding-bottom: env(safe-area-inset-bottom);
    padding-bottom: constant(safe-area-inset-bottom);

    svg {
      cursor: pointer;
    }

    .sort_list {
      padding: 0 16px;
    }

    .product_sort_title {
      .left {
        margin-left: 32px;
      }
    }
  `,
};
